import {
  EditOutlined,
  EyeOutlined,
  LeftOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { faTree, faVectorSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  DrawButton,
  LayerTree,
  MapComponent,
  MapContext,
  ToggleGroup,
} from "@terrestris/react-geo";
import { Button, Col, Divider, Popover, Result, Row, Space } from "antd";
import Title from "antd/lib/typography/Title";
import CommonOverlayComponent from "commons/CommonOverlayComponent";
import { CommonFormAntActions } from "commons/Forms/CommonFormAnt";
import CommonCard from "commons/Layout/CommonCard";
import { openNotification } from "commons/Notifications";
import { AuthContext } from "context/auth";
import OlMap from "ol/Map";
import OlView from "ol/View";
import { getCenter } from "ol/extent";
import GeoJSON from "ol/format/GeoJSON";
import { LineString, Point, Polygon } from "ol/geom";
import OlLayerTile from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import { bbox as bboxStrategy } from "ol/loadingstrategy";
import BingMaps from "ol/source/BingMaps";
import OlSourceOsm from "ol/source/OSM";
import VectorSource from "ol/source/Vector";
import { Circle, Fill, Stroke, Style } from "ol/style";
import { FC, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ServiceAirTree from "services/airTree";
import ServiceGeoServer from "services/geoServer";
import transactWFS from "utils/helpers/WFSTransactions";
import { DateNow, FormatDate } from "utils/helpers/date_helpers";
import {
  geoFormatArea,
  geoFormatLength,
  getGeomTypeLabel,
} from "utils/helpers/geo_helpers";
import { v4 as uuidv4 } from "uuid";
import { AirTreeFormFields } from "./components/elementFormFields";
import { AirTreeProjectFormFields } from "./components/projectFormFields";

const AirTreeEditProject: FC = () => {
  const { id } = useParams();

  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [drawerEditProjectVisible, setDrawerEditProjectVisible] =
    useState(null);
  const [geoFeature, setGeoFeature] = useState(null);
  const [geoType, setGeoType] = useState(null);
  const [currentForm, setCurrentForm] = useState(null);
  const [projectHasElements, setProjectHasElements] = useState(false);
  const [authorized, setAuthorized] = useState(true);
  const [loadingStartSimulation, setLoadingStartSimulation] = useState(false);
  const [simulationDateRange, setSimulationDateRange] = useState(null);

  // Map and Vector layers
  const [map, setMap] = useState(null);
  const [mainBbox, setMainBbox] = useState(null);
  const [projectVector, setProjectVector] = useState(null);
  const [projectElementsVector, setProjectElementsVector] = useState(null);
  const [reloadCurrentProject, setReloadCurrentProject] = useState(false);
  const Form = useRef<CommonFormAntActions>(undefined);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const zoom = params.get("zoom");

  const geoServerUrl = process?.env?.REACT_APP_GEOSERVER_URL;

  useEffect(() => {
    // Get Simulation Date Range
    ServiceAirTree.getRangeSimulationDate()
      .then((res) => {
        setSimulationDateRange(res);
      })
      .catch((e) => {
        console.log("Error: ", e);
      });

    if (id) {
      // Get project
      ServiceAirTree.get({ id: parseInt(id) })
        .then((res) => {
          setCurrentProject(res);
        })
        .catch((e) => {
          console.log("Error: ", e);
        });
    } else {
      setCurrentProject(null);
    }
  }, [id, reloadCurrentProject]);

  useEffect(() => {
    if (currentProject) {
      if (currentProject?.created_by !== currentUser?.id) {
        setAuthorized(false);
      }

      // Get Main Project Vector
      ServiceGeoServer.getProjectWfs({ uuid: currentProject?.uuid }).then(
        (wfsProj) => {
          if (wfsProj.bbox) {
            setMainBbox(wfsProj.bbox);
          }

          const vectorSource = new VectorSource({
            features: new GeoJSON().readFeatures(wfsProj),
            strategy: bboxStrategy,
          });

          const vectorProject = new VectorLayer({
            // @ts-ignore
            name: "",
            source: vectorSource,
            style: new Style({
              fill: new Fill({
                color: "rgba(255, 255, 255, 0.1)",
              }),
              stroke: new Stroke({
                color: "rgba(253, 52, 20, 1.0)",
                width: 3,
              }),
            }),
          });
          setProjectVector(vectorProject);
        }
      );

      // Get Project Elements Vector
      ServiceGeoServer.getProjectElementsWfs({
        idProject: currentProject?.id,
      }).then((wfsProjElements) => {
        if (wfsProjElements?.features?.length > 0) {
          setProjectHasElements(true);
        }
        const vectorProjectElementsSource = new VectorSource({
          features: new GeoJSON().readFeatures(wfsProjElements),
          strategy: bboxStrategy,
        });

        // const fill = new Fill({
        //   color: '#ffff00',
        // });

        // const stroke = new Stroke({
        //   color: '#ffff88',
        //   width: 3,
        // });

        const vectorProjectElements = new VectorLayer({
          // @ts-ignore
          name: "",
          source: vectorProjectElementsSource,
          style: function (feature, resolution) {
            // If point set number of trees
            if (feature?.getGeometry() instanceof Point) {
              return new Style({
                image: new Circle({
                  fill: new Fill({
                    color: "rgba(46, 204, 113, 0.7)",
                  }),
                  stroke: new Stroke({
                    color: "rgba(46, 204, 113, 1)",
                  }),
                  radius: 6,
                }),
              });
            }

            // If line get measure
            if (feature?.getGeometry() instanceof LineString) {
              return new Style({
                stroke: new Stroke({
                  color: "rgba(255, 229, 10, 1)",
                  width: 3,
                }),
              });
            }

            // If polygon get area
            if (feature?.getGeometry() instanceof Polygon) {
              return new Style({
                fill: new Fill({
                  color: "rgba(52, 152, 219, 0.6)",
                }),
                stroke: new Stroke({
                  color: "rgba(52, 152, 219, 1)",
                  width: 3,
                }),
              });
            }
          },
          // style: new Style({
          //   image: new Circle({
          //     fill: fill,
          //     stroke: stroke,
          //     radius: 10,
          //   }),
          //   fill: fill,
          //   stroke: stroke
          // }),
        });
        setProjectElementsVector(vectorProjectElements);
      });
    }
  }, [currentProject]);

  useEffect(() => {
    if (currentProject) {
      if (mainBbox && projectVector && projectElementsVector) {
        setMap(
          new OlMap({
            layers: [
              new OlLayerTile({
                // @ts-ignore
                name: "OSM",
                source: new OlSourceOsm(),
              }),
              new OlLayerTile({
                // @ts-ignore
                name: "Satellite",
                source: new BingMaps({
                  key: "AgvK-EavqnKlSbi2mfp_nKxT5RpmBZLr4N9k1ChJeW1v8ePqaUEUSy2WYDllrYRH",
                  imagerySet: "Aerial",
                }),
              }),
              projectVector,
              projectElementsVector,
            ],
            view: new OlView({
              //projection: 'EPSG:4326',
              //extent: currentProject.bbox
              center: getCenter(mainBbox),
              zoom: zoom ? parseInt(zoom) : 12,
            }),
          })
        );
      }
    }
  }, [currentProject, mainBbox, projectVector, projectElementsVector]);

  if (!map) {
    return null;
  }

  const updateFieldLai = (val, indexField = null) => {
    let laiVal =
      4.3693 * Math.exp(0.00048204 * val) - 4.5985 * Math.exp(-0.062827 * val);
    let valuesObj = {};
    if (indexField) {
      valuesObj[`area_${indexField}`] = { lai: laiVal };
    } else {
      valuesObj["lai"] = laiVal;
    }

    Form.current.setFormValues(valuesObj);
  };

  const updateFieldDepth = (val, indexField = null) => {
    let depthVal = val * 0.7;
    let valuesObj = {};
    if (indexField) {
      valuesObj[`area_${indexField}`] = { depth: depthVal };
    } else {
      valuesObj["lai"] = depthVal;
    }
    Form.current.setFormValues(valuesObj);
  };

  const updateFieldsFromType = async (val, indexField = null) => {
    let valuesObj = {
      height: null,
      dbh: null,
      lai: null,
      depth: null,
      crownwidth: null,
    };

    // Select type is empty
    if (!val) {
      if (indexField) {
        valuesObj[`area_${indexField}`] = { ...valuesObj };
      }
      Form.current.setFormValues(valuesObj);
    } else {
      await ServiceAirTree.getTreeType(parseInt(val))
        .then((res) => {
          if (indexField) {
            valuesObj[`area_${indexField}`] = { ...res };
          } else {
            valuesObj = { ...res };
          }

          Form.current.setFormValues(valuesObj);
        })
        .catch((e) => {
          console.log("Error: ", e);
        });
    }
  };

  const setProjectElement = (feature, type) => {
    let form = AirTreeFormFields({
      updateFieldLai,
      updateFieldDepth,
      type,
      updateFieldsFromType,
    });
    setGeoType(type);
    setGeoFeature(feature);
    setCurrentForm(form);
    setDrawerVisible(true);
  };

  // Submit form
  const onSubmit = async (values: any) => {
    // If point set number of trees
    if (geoFeature?.getGeometry() instanceof Point) {
      // Set area of polygon and number_of_trees
      values.number_of_trees = 1;
    }

    // If line get measure
    if (geoFeature?.getGeometry() instanceof LineString) {
      // Set length of line and number_of_trees
      const distance = geoFormatLength(geoFeature?.getGeometry());
      values.line_length = distance;
      if (values?.tree_number_or_distance === 2 && values?.distance_of_trees) {
        values.number_of_trees = Math.round(
          distance / (values?.crownwidth + values?.distance_of_trees)
        );
      }
    }

    // If polygon get area
    let shapeValues = null;
    if (geoFeature?.getGeometry() instanceof Polygon) {
      const area = geoFormatArea(geoFeature?.getGeometry(), "m");
      // Set area of polygon and number_of_trees
      for (let index = 0; index < 3; index++) {
        const incrementedIndex = index + 1;

        if (values[`area_${incrementedIndex}`]?.type_cover_percent) {
          // Calculate the area of the percent tree coverage
          const areaTree =
            (area * values[`area_${incrementedIndex}`].type_cover_percent) /
            100;

          // Calculate the radius of the canopy
          const canopyRadius =
            values[`area_${incrementedIndex}`].crownwidth / 2;
          const singleAreaTree = Math.PI * Math.pow(canopyRadius, 2);
          const numberOfTrees = Math.round(areaTree / singleAreaTree);

          values[`area_${incrementedIndex}`].polygon_area = area;
          values[`area_${incrementedIndex}`].tree_type_area = areaTree;
          values[`area_${incrementedIndex}`].number_of_trees = numberOfTrees;
        }

        // if (values[`area_${incrementedIndex}`]?.type_cover_percent) {
        //   const percent_type_area =
        //     (area / 100) *
        //     values[`area_${incrementedIndex}`].type_cover_percent;
        //   const covered_area =
        //     (percent_type_area / 100) *
        //     values[`area_${incrementedIndex}`].canopy_cover;
        //   const single_area_tree =
        //     Math.PI *
        //     (Math.pow(values[`area_${incrementedIndex}`].crownwidth, 2) / 4); // Pi greco * (diametro2/4)
        //   values[`area_${incrementedIndex}`].projectArea = area;
        //   values[`area_${incrementedIndex}`].number_of_trees = Math.round(
        //     covered_area / single_area_tree
        //   );
        // }
      }
      shapeValues = Object.keys(values).map((key) => {
        return { key: key, ...values[key] };
      });
    }

    // Set properties
    let properties = {};
    //properties = { ...values };
    properties["id_project"] = id;
    properties["uuid"] = uuidv4();
    properties["geom_type"] = geoType;
    properties["details"] = shapeValues
      ? JSON.stringify([...shapeValues])
      : JSON.stringify({ ...values });
    properties["created_by"] = currentUser?.id;
    properties["updated_by"] = currentUser?.id;
    properties["created_at"] = DateNow("X");
    properties["updated_at"] = DateNow("X");

    // Add properties to geo feature
    geoFeature.setProperties(properties);

    let writeParams = {
      mode: "insert",
      features: [geoFeature],
      layerCompleteName: "airtree:airtree_project_elements",
      serverUrl: geoServerUrl,
      callback: function (r) {
        console.log("Response", r);
      },
    };

    try {
      transactWFS(writeParams);
    } catch (error) {
      console.log(
        "🚀 ~ file: editProject.tsx ~ line 353 ~ onSubmit ~ error",
        error
      );
    }

    setGeoFeature(false);
    window.location.href =
      window.location.href.split("?")[0] +
      `?zoom=${map.getView().getZoom().toFixed(2)}`;
    window.location.reload();
  };

  // Submit Edit Project form
  const onSubmitEditProject = async (values: any) => {
    if (values) {
      // Update project
      ServiceAirTree.update({ id: currentProject?.id, data: values }).then(
        (success) => {
          openNotification({
            message: "OK",
            description: "Scheda salvata correttamente",
            type: "success",
          });
          setDrawerEditProjectVisible(false);
          window.location.reload();
        }
      );
    }
  };

  const startSimulation = () => {
    setLoadingStartSimulation(true);
    // Start simulation
    ServiceAirTree.startSimulation(currentProject?.id).then((success) => {
      openNotification({
        message: "OK",
        description:
          "Simulazione inviata correttamente, riceverai una mail al termine dell'elaborazione",
        type: "success",
      });
      setLoadingStartSimulation(true);
      setReloadCurrentProject(true);
      // if (airtreeProjectTable.current) {
      //   airtreeProjectTable.current.refresh();
      // }
    });
  };

  if (!authorized) {
    return (
      <Result
        status="403"
        title="403"
        subTitle="Non sei autorizzato ad accedere a questa risorsa."
        extra={
          <Button type="primary" onClick={() => navigate("/")}>
            Home
          </Button>
        }
      />
    );
  }

  return (
    <CommonCard title={<h2>Progetto: {currentProject?.name}</h2>}>
      <h3>
        Data inizio simulazione:{" "}
        {FormatDate(currentProject?.simulation_date_start)} - Data fine
        simulazione: {FormatDate(currentProject?.simulation_date_end)}
        {currentProject?.status === 0 && (
          <Button
            onClick={() => setDrawerEditProjectVisible(true)}
            style={{
              marginLeft: 10,
            }}
            type="text"
            shape="circle"
            icon={<EditOutlined />}
          />
        )}
      </h3>

      <Divider />
      <Row gutter={16}>
        <Col xs={24} sm={24} md={24} lg={24} xl={24}>
          <MapContext.Provider value={map}>
            <Row>
              <Col xs={24} sm={24} md={24} lg={24} xl={16}>
                {currentProject?.status === 0 && (
                  <ToggleGroup orientation="horizontal">
                    <DrawButton
                      style={{ marginRight: "10px" }}
                      tooltip="Disegna un singolo albero"
                      icon={<FontAwesomeIcon icon={faTree} />}
                      pressedIcon={<FontAwesomeIcon icon={faTree} />}
                      // drawStyle={new Style({
                      //   image: new Circle({
                      //     radius: 3 * 2,
                      //     fill: new Fill({
                      //       color: [0, 153, 255, 1],
                      //     }),
                      //     stroke: new Stroke({
                      //       color: [255, 255, 255, 1],
                      //       width: 3 / 2,
                      //     }),
                      //   })
                      // })}
                      pressed={geoFeature ? true : false}
                      name="drawPoint"
                      drawType="Point"
                      onDrawEnd={({ feature }) => setProjectElement(feature, 1)}
                    />
                    <DrawButton
                      style={{ marginRight: "10px" }}
                      tooltip="Disegna un filare"
                      icon={<EditOutlined />}
                      pressedIcon={<EditOutlined />}
                      name="drawLine"
                      drawType="LineString"
                      onDrawEnd={({ feature }) => setProjectElement(feature, 2)}
                    />
                    <DrawButton
                      style={{ marginRight: "10px" }}
                      tooltip="Disegna una area boschiva"
                      icon={<FontAwesomeIcon icon={faVectorSquare} />}
                      pressedIcon={<FontAwesomeIcon icon={faVectorSquare} />}
                      name="drawPolygon"
                      drawType="Polygon"
                      //onDrawEnd={({ feature }) => setProjectElement(feature, 3)}
                      onDrawEnd={({ feature }) => setProjectElement(feature, 3)}
                    />
                  </ToggleGroup>
                )}

                {currentProject?.status == 1 && (
                  <Title level={4} type="warning">
                    Simulazione inviata in attesa di risultati
                  </Title>
                )}
              </Col>
              <Col
                xs={24}
                sm={24}
                md={24}
                lg={24}
                xl={8}
                style={{ textAlign: "right" }}
              >
                <Space>
                  {currentProject?.status === 0 && projectHasElements && (
                    <Button
                      type="primary"
                      icon={<SettingOutlined />}
                      style={{ background: "#13c2c2", borderColor: "#08979c" }}
                      onClick={() => startSimulation()}
                      loading={loadingStartSimulation}
                    >
                      Avvia simulazione
                    </Button>
                  )}

                  {currentProject?.status >= 1 && (
                    <Button
                      type="default"
                      icon={<LeftOutlined />}
                      onClick={() =>
                        navigate(`/airtree-list`, { replace: true })
                      }
                    >
                      Torna alla lista progetti
                    </Button>
                  )}
                </Space>
              </Col>
              <Col
                xs={24}
                sm={24}
                md={24}
                lg={24}
                xl={24}
                style={{ paddingTop: 20 }}
              >
                <Popover
                  content={
                    <LayerTree
                      map={map}
                      filterFunction={(layer) => {
                        return (
                          layer.get("name") !== "" &&
                          layer.get("name") !== "OSM" &&
                          layer.get("name") !== "react-geo_digitize"
                        );
                      }}
                    />
                  }
                  title="Selezione layers"
                  trigger="click"
                >
                  <Button
                    style={{
                      position: "absolute",
                      top: "40px",
                      left: "50px",
                      zIndex: 2,
                    }}
                    type="default"
                    shape="circle"
                    icon={<EyeOutlined />}
                  />
                </Popover>

                <MapContext.Consumer>
                  {(map) => {
                    return (
                      <MapComponent
                        map={map}
                        style={{
                          height: "80vh",
                        }}
                      />
                    );
                  }}
                </MapContext.Consumer>
              </Col>
            </Row>
          </MapContext.Provider>
        </Col>
      </Row>

      <CommonOverlayComponent
        title={`Scheda inserimento ${getGeomTypeLabel(geoType)}`}
        onSubmit={onSubmit}
        type="modal"
        destroyOnClose={true}
        //permission={DisabledFields}
        visible={drawerVisible}
        width={"60%"}
        okText="Conferma e Salva"
        onCancel={() => {
          setDrawerVisible(false);
        }}
        formsOpts={{
          debug: false,
          fields: currentForm,
          ref: Form,
          hideSubmit: true,
          ...(geoType === 3 && { tabForm: "card" }),
          //initialValues: currentProject || undefined,
        }}
      />

      <CommonOverlayComponent
        title={`Scheda progetto`}
        onSubmit={onSubmitEditProject}
        type="modal"
        destroyOnClose={true}
        //permission={DisabledFields}
        visible={drawerEditProjectVisible}
        width={"60%"}
        okText="Conferma e Salva"
        onCancel={() => setDrawerEditProjectVisible(false)}
        formsOpts={{
          debug: false,
          fields: AirTreeProjectFormFields({ simulationDateRange }),
          //ref: Form,
          hideSubmit: true,
          initialValues: currentProject || undefined,
        }}
      />
    </CommonCard>
  );
};

export default AirTreeEditProject;
